import { useLocation, useNavigate, useSearchParams } from "@solidjs/router";
import { createEffect, createRenderEffect, For } from "solid-js";
import { createStore } from "solid-js/store";
import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { TbX } from "solid-icons/tb";
import { StButton } from "@storytell/ui/buttons";
import { useWire } from "~/wire";

const products = ["pro_monthly", "pro_yearly", "team", "credits"] as const;

type StripeProduct = (typeof products)[number];
type StripeState =
  | { type: "none" }
  | { type: "success"; product: StripeProduct; quantity: number }
  | { type: "cancel" };

export const getStripeCheckoutSuccessParam = (product: StripeProduct, quantity: number) =>
  `stripe_success=${product};${quantity}`;

export const useStripeURLReturnState = () => {
  const [state, setState] = createStore<StripeState>({
    type: "none",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  createRenderEffect(() => {
    if (typeof params["stripe_success"] === "string") {
      const [product, quantityStr] = params["stripe_success"].split(";");
      if (!products.includes(product as StripeProduct)) {
        return;
      }
      let quantity = 1;
      try {
        quantity = Number.parseInt(quantityStr);
      } catch (err) {}

      setState({
        type: "success",
        product: product as StripeProduct,
        quantity,
      });

      return;
    }
    if (params["stripe_cancel"] === "true") {
      setState("type", "cancel");
      return;
    }
    setState("type", "none");
  });

  const close = () => {
    navigate(location.pathname, { replace: true });
  };

  return [state, { close }] as const;
};

export const StripeReturnModal = () => {
  const [state, { close }] = useStripeURLReturnState();
  const wire = useWire();

  const title = () => {
    if (state.type === "success") {
      return ["pro_monthly", "pro_yearly"].includes(state.product)
        ? "Thanks for going Pro!"
        : "Your credits have been refilled!";
    }
    return "";
  };

  const description = () => {
    if (state.type === "success") {
      if (state.product === "pro_monthly") {
        return [
          "We've updated your credits balance.",
          `You will receive ${(20000).toLocaleString()} credits each month.`,
        ];
      }
      if (state.product === "pro_yearly") {
        return [
          "We've updated your credits balance.",
          `You will receive ${(240000).toLocaleString()} credits each year.`,
        ];
      }
      if (state.product === "credits") {
        // TODO: Get credits amount from BE.
        const multiplier = wire.auth.subscription()?.planType === "pro.v1" ? 1.25 : 1;
        return [`We've added ${state.quantity * multiplier} credits to your balance.`];
      }
    }
  };

  return (
    <AnimateChildren
      show={state.type !== "none"}
      enter={{ opacity: [0, 1], y: [10, 0] }}
      exit={{ opacity: [1, 0], y: [0, 10] }}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) close();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed z-90 inset-0 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed z-90 inset-0 flex items-center justify-center">
              <Dialog.Content class="max-h-[80vh] relative w-screen text-primary-text max-w-xl bg-panel overflow-y-auto rounded-xl p-8">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>
                <div class="flex items-start gap-2 mb-3 justify-between">
                  <h3 class="text-lg font-medium">{title()}</h3>
                </div>

                <For each={description()}>{(desc) => <p class="text-sm text-secondary-text">{desc}</p>}</For>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
